/**
 * Teacher Dashboard
 * Overview of assigned courses with scheduled classes
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '../../components';
import api from '../../services/api';
import { formatCourseThumbnail, handleThumbnailError } from '../../utils/imageUtils';
import SEO from '../../components/common/SEO';

const formatClassDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatClassTime = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
};

const TeacherDashboard = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [stats, setStats] = useState({
    totalCourses: 0,
    totalStudents: 0,
    avgRating: 0,
  });

  useEffect(() => {
    fetchCourses();
  }, [filter]);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const data = await api.getAssignedCourses(filter);
      setCourses(data.courses || []);

      const totalStudents = data.courses?.reduce((sum, c) => sum + (c.enrollmentCount || 0), 0) || 0;
      const avgRating = data.courses?.length > 0
        ? data.courses.reduce((sum, c) => sum + (c.rating?.average || 0), 0) / data.courses.length
        : 0;

      setStats({
        totalCourses: data.courses?.length || 0,
        totalStudents,
        avgRating: parseFloat(avgRating.toFixed(1)),
      });
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const now = new Date();

  return (
    <div className="lh-teacher-dashboard min-h-screen bg-dark-50">
      <SEO title="Teacher Dashboard" noIndex />
      <Navbar />

      <main className="lh-teacher-dashboard__main pt-20 pb-12">
        <div className="lh-teacher-dashboard__container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="lh-teacher-dashboard__header mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-dark-900 mb-1">
              Teacher Dashboard
            </h1>
            <p className="text-dark-500">
              View your assigned courses and scheduled classes
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="card p-5">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <p className="text-2xl font-bold text-dark-900">{stats.totalCourses}</p>
              <p className="text-sm text-dark-500">Assigned Courses</p>
            </div>
            <div className="card p-5">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <p className="text-2xl font-bold text-dark-900">{stats.totalStudents}</p>
              <p className="text-sm text-dark-500">Total Students</p>
            </div>
            <div className="card p-5">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                </svg>
              </div>
              <p className="text-2xl font-bold text-dark-900">{stats.avgRating || 0}</p>
              <p className="text-sm text-dark-500">Avg Rating</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-3 mb-6">
            {['all', 'upcoming', 'completed'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  filter === f ? 'bg-primary-500 text-white' : 'bg-white text-dark-600 hover:bg-dark-100'
                }`}
              >
                {f === 'all' ? `All Courses (${courses.length})` :
                 f === 'upcoming' ? `Upcoming (${courses.length})` :
                 `Completed (${courses.length})`}
              </button>
            ))}
          </div>

          {/* Course List */}
          {loading ? (
            <div className="card p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto" />
              <p className="mt-4 text-dark-500">Loading courses...</p>
            </div>
          ) : courses.length > 0 ? (
            <div className="space-y-4">
              {courses.map((course) => {
                const assignedClasses = course.assignedClasses || [];
                const upcomingClasses = assignedClasses.filter(c => new Date(c.scheduledDate) >= now);
                const pastClasses = assignedClasses.filter(c => new Date(c.scheduledDate) < now);

                return (
                  <div key={course._id} className="card overflow-hidden">
                    <div className="flex flex-col sm:flex-row">
                      <img
                        src={formatCourseThumbnail(course.thumbnail)}
                        alt={course.title}
                        className="w-full sm:w-40 h-32 object-cover"
                        onError={handleThumbnailError}
                      />
                      <div className="flex-1 p-4">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex-1">
                            <h3 className="font-semibold text-dark-900 text-lg">{course.title}</h3>
                            <div className="flex flex-wrap gap-3 mt-1 text-sm text-dark-500">
                              <span className="flex items-center gap-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                {course.enrollmentCount || 0} students
                              </span>
                              <span className="flex items-center gap-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                {assignedClasses.length} class{assignedClasses.length !== 1 ? 'es' : ''} assigned
                              </span>
                              {upcomingClasses.length > 0 && (
                                <span className="bg-blue-100 text-blue-600 text-xs font-medium px-2 py-0.5 rounded-full">
                                  {upcomingClasses.length} upcoming
                                </span>
                              )}
                            </div>
                          </div>
                          <span className={`text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0 ${
                            course.status === 'published'
                              ? 'bg-green-100 text-green-600'
                              : 'bg-dark-100 text-dark-600'
                          }`}>
                            {course.status === 'published' ? 'Published' : 'Draft'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Assigned Classes */}
                    {assignedClasses.length > 0 && (
                      <div className="border-t border-dark-100">
                        {/* Upcoming classes */}
                        {upcomingClasses.length > 0 && (
                          <div>
                            <div className="px-4 pt-3 pb-1">
                              <p className="text-xs font-semibold text-dark-400 uppercase tracking-wide">Your Upcoming Classes</p>
                            </div>
                            <div className="divide-y divide-dark-50">
                              {upcomingClasses.map((cls, i) => {
                                const isNext = i === 0;
                                return (
                                  <div key={cls._id} className={`px-4 py-3 flex items-center gap-4 ${isNext ? 'bg-primary-50/40' : 'hover:bg-dark-50'} transition-colors`}>
                                    <div className={`flex-shrink-0 w-14 text-center rounded-lg py-1.5 ${isNext ? 'bg-primary-500 text-white' : 'bg-dark-100 text-dark-600'}`}>
                                      <p className="text-xs font-medium leading-tight">
                                        {new Date(cls.scheduledDate).toLocaleDateString('en-IN', { month: 'short' })}
                                      </p>
                                      <p className="text-lg font-bold leading-tight">
                                        {new Date(cls.scheduledDate).getDate()}
                                      </p>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <h4 className="font-medium text-dark-900 text-sm">{cls.title}</h4>
                                        {isNext && <span className="text-xs bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded font-medium">Next</span>}
                                      </div>
                                      <p className="text-xs text-dark-500 mt-0.5">
                                        {formatClassDate(cls.scheduledDate)} at {formatClassTime(cls.scheduledDate)}
                                        {cls.duration ? ` · ${cls.duration} min` : ''}
                                      </p>
                                    </div>
                                    {cls.meetingLink ? (
                                      <a
                                        href={cls.meetingLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                          isNext ? 'bg-primary-500 text-white hover:bg-primary-600' : 'border border-primary-300 text-primary-600 hover:bg-primary-50'
                                        }`}
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        Join
                                      </a>
                                    ) : (
                                      <span className="flex-shrink-0 text-xs text-dark-400 py-2">Link pending</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Past classes */}
                        {pastClasses.length > 0 && (
                          <div className="border-t border-dark-100 px-4 py-3">
                            <details className="group">
                              <summary className="text-xs font-medium text-dark-400 cursor-pointer hover:text-dark-600 list-none flex items-center gap-1">
                                <svg className="w-3.5 h-3.5 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                {pastClasses.length} past class{pastClasses.length !== 1 ? 'es' : ''}
                              </summary>
                              <div className="mt-2 divide-y divide-dark-50">
                                {pastClasses.map((cls) => (
                                  <div key={cls._id} className="flex items-center gap-4 py-2 text-dark-400">
                                    <span className="w-14 text-center text-xs flex-shrink-0">
                                      {formatClassDate(cls.scheduledDate)}
                                    </span>
                                    <span className="text-sm flex-1">{cls.title}</span>
                                    <span className="text-xs">
                                      {formatClassTime(cls.scheduledDate)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </details>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="card p-12 text-center">
              <svg className="w-16 h-16 text-dark-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <h3 className="text-lg font-semibold text-dark-900 mb-1">No courses assigned</h3>
              <p className="text-dark-500">You don't have any courses assigned yet. Contact admin to get courses assigned.</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TeacherDashboard;
